import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useBranding } from "@/lib/branding";
import { Button } from "@/components/ui/button";
import { LogOut, LayoutDashboard, GraduationCap, ClipboardCheck, Sparkles, Award, Settings, Bookmark, MessageCircle, Users, Newspaper, Wallet, KeyRound, ClipboardList, FilePenLine, Calendar, User, UserPlus } from "lucide-react";
import PoweredBy from "@/components/PoweredBy";
import AdminNotificationBadge from "@/components/AdminNotificationBadge";

const NavLink = ({ to, icon: Icon, children, testid }) => {
  const loc = useLocation();
  const active = loc.pathname === to || (to !== "/dashboard" && loc.pathname.startsWith(to));
  return (
    <Link
      data-testid={testid}
      to={to}
      className={`flex items-center gap-3 px-4 py-3 rounded-md transition-colors text-sm ${
        active ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-secondary"
      }`}
    >
      <Icon size={16} />
      <span>{children}</span>
    </Link>
  );
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { branding } = useBranding();
  const nav = useNavigate();

  const handleLogout = () => { logout(); nav("/login"); };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 lg:w-72 border-r border-border flex-col justify-between p-5 lg:p-6 bg-card sticky top-0 h-screen overflow-y-auto">
        <div>
          <Link to="/dashboard" data-testid="brand-link" className="flex items-center gap-3 mb-10">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt="Logo" className="h-9 w-9 object-contain" />
            ) : (
              <div className="h-9 w-9 rounded-md bg-primary flex items-center justify-center text-primary-foreground font-serif text-lg">
                {branding.brand_name?.[0] || "T"}
              </div>
            )}
            <div>
              <div className="font-serif text-xl leading-none">{branding.brand_name}</div>
              <div className="label-caps mt-1 text-[10px]">Est. 2026</div>
            </div>
          </Link>

          <nav className="space-y-1">
            <div className="label-caps px-4 mb-2">Learn</div>
            <NavLink to="/dashboard" icon={LayoutDashboard} testid="nav-dashboard">Dashboard</NavLink>
            <NavLink to="/course" icon={GraduationCap} testid="nav-course">Course</NavLink>
            <NavLink to="/bookmarks" icon={Bookmark} testid="nav-bookmarks">Bookmarks</NavLink>
            <NavLink to="/practice" icon={ClipboardCheck} testid="nav-practice">Practice 1040</NavLink>
            <NavLink to="/exam" icon={Sparkles} testid="nav-exam">Final Exam</NavLink>
            <NavLink to="/certificate" icon={Award} testid="nav-certificate">Certificate</NavLink>
            <NavLink to="/questions" icon={MessageCircle} testid="nav-questions">Ask Instructor</NavLink>
            {user?.role === "admin" && (
              <>
                <div className="label-caps px-4 mb-2 mt-6">Admin</div>
                <AdminNotificationBadge />
                <NavLink to="/admin/dashboard" icon={LayoutDashboard} testid="nav-admin-dashboard">Overview</NavLink>
                <NavLink to="/admin" icon={Settings} testid="nav-admin">Branding</NavLink>
                <NavLink to="/admin/instructor-bio" icon={User} testid="nav-instructor-bio">Meet-me page</NavLink>
                <NavLink to="/admin/students" icon={Users} testid="nav-students">Students</NavLink>
                <NavLink to="/admin/payments" icon={Wallet} testid="nav-payments">Pending payments</NavLink>
                <NavLink to="/admin/password-resets" icon={KeyRound} testid="nav-resets">Password resets</NavLink>
                <NavLink to="/admin/intake" icon={ClipboardList} testid="nav-intake">Intake forms</NavLink>
                <NavLink to="/admin/amendments" icon={FilePenLine} testid="nav-amendments">Amendments</NavLink>
                <NavLink to="/admin/appointments" icon={Calendar} testid="nav-appointments">Appointments</NavLink>
                <NavLink to="/admin/team" icon={UserPlus} testid="nav-team">Team invites</NavLink>
                <NavLink to="/admin/afsp-cohort" icon={Award} testid="nav-afsp-cohort">AFSP cohort</NavLink>
                <NavLink to="/admin/digest" icon={Newspaper} testid="nav-digest">Weekly digest</NavLink>
              </>
            )}
          </nav>
        </div>

        <div className="border-t border-border pt-4">
          <div className="text-sm mb-3">
            <div className="font-medium truncate" data-testid="sidebar-user-name">{user?.name}</div>
            <div className="text-muted-foreground text-xs truncate">{user?.email}</div>
          </div>
          <Button data-testid="logout-btn" variant="outline" size="sm" onClick={handleLogout} className="w-full">
            <LogOut size={14} className="mr-2" /> Sign out
          </Button>
          <PoweredBy className="mt-4 text-center" />
        </div>
      </aside>

      {/* Compact top bar (phones only) */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 flex items-center justify-between px-4 h-14 bg-background/80 backdrop-blur-xl border-b border-border">
        <Link to="/dashboard" className="flex items-center gap-2">
          {branding.logo_url ? <img src={branding.logo_url} className="h-7" alt="" /> : <div className="h-7 w-7 rounded bg-primary text-primary-foreground grid place-items-center font-serif">{branding.brand_name?.[0]}</div>}
          <span className="font-serif text-lg">{branding.brand_name}</span>
        </Link>
        <Button data-testid="logout-btn-mobile" variant="ghost" size="sm" onClick={handleLogout}><LogOut size={16} /></Button>
      </div>

      {/* Compact bottom nav (phones only) */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-40 grid grid-cols-5 border-t border-border bg-background/95 backdrop-blur">
        {[
          {to: "/dashboard", icon: LayoutDashboard, label: "Home"},
          {to: "/course", icon: GraduationCap, label: "Course"},
          {to: "/bookmarks", icon: Bookmark, label: "Saved"},
          {to: "/practice", icon: ClipboardCheck, label: "Practice"},
          {to: "/exam", icon: Sparkles, label: "Exam"},
        ].map(item => {
          const Ic = item.icon;
          return (
            <Link key={item.to} to={item.to} className="flex flex-col items-center justify-center py-2 text-[11px] text-foreground">
              <Ic size={18} />
              <span className="mt-0.5">{item.label}</span>
            </Link>
          );
        })}
      </div>

      <main className="flex-1 min-w-0 md:pl-0 pt-14 md:pt-0 pb-20 md:pb-0 paper-texture">
        {user?.on_trial && user?.trial_days_left > 0 && (
          <div className="bg-accent/15 border-b border-accent px-4 py-2 flex flex-wrap items-center justify-between gap-2" data-testid="trial-banner">
            <div className="text-sm">
              <span className="label-caps text-accent">Instructor trial · </span>
              <strong>{user.trial_days_left}</strong> day{user.trial_days_left === 1 ? "" : "s"} left.
              <span className="text-muted-foreground ml-2 hidden sm:inline">After that your account reverts to Student unless you enroll.</span>
            </div>
            <Link
              to="/pricing"
              className="text-sm underline text-foreground shrink-0"
              data-testid="trial-upgrade-link"
            >
              Enroll now →
            </Link>
          </div>
        )}
        {children}
      </main>
    </div>
  );
}
